<script setup>
import Header from "@/components/Header.vue";
import heroImage from "@/assets/img/Evolve/img_8.jpg";
import { loadSlim } from "tsparticles-slim";
import { getPatrocinadoresConfirmados } from "@/utils/api_evento";
import { ref, onMounted } from "vue";

const patrocinadores = ref([]);
const cargando = ref(true);

const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#cc0000" },
    shape: { type: "circle" },
    opacity: { value: 0.25 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};

const particlesInit = async (engine) => {
  await loadSlim(engine);
};

const redSocial = (patrocinador, clave) => patrocinador?.redes_sociales?.[clave] || null;

onMounted(async () => {
  try {
    patrocinadores.value = await getPatrocinadoresConfirmados();
  } catch (error) {
    console.error("Error al cargar patrocinadores:", error);
  } finally {
    cargando.value = false;
  }
});
</script>

<template>
  <section id="patrocinadores" class="patro-wrapper">
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="patroParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>

    <div class="patro-content">
      <Header />

      <div class="patro-section" data-aos="fade-up">
        <div class="titulo-row" data-aos="fade-up">
          <span class="titulo-linea"></span>
          <h2 class="titulo-seccion">Patrocinadores <em>Estelares</em></h2>
          <span class="titulo-linea"></span>
        </div>

        <div
          v-if="!cargando && patrocinadores.length"
          class="patro-grid"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <a
            v-for="p in patrocinadores"
            :key="p.id"
            :href="redSocial(p, 'sitio_web') || undefined"
            :target="redSocial(p, 'sitio_web') ? '_blank' : undefined"
            rel="noopener noreferrer"
            class="patro-card"
            :class="{ 'patro-card--link': redSocial(p, 'sitio_web') }"
          >
            <div class="patro-logo-plate">
              <img :src="p.logo" :alt="p.nombre_marca" loading="lazy" />
            </div>

            <p class="patro-nombre">{{ p.nombre_marca }}</p>

            <div
              class="patro-redes"
              v-if="
                redSocial(p, 'instagram') ||
                redSocial(p, 'facebook') ||
                redSocial(p, 'tiktok') ||
                redSocial(p, 'youtube')
              "
            >
              <a
                v-if="redSocial(p, 'instagram')"
                :href="redSocial(p, 'instagram')"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                <img src="@/assets/img/Evolve/instagram.png" alt="Instagram" />
              </a>
              <a
                v-if="redSocial(p, 'facebook')"
                :href="redSocial(p, 'facebook')"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                <img src="@/assets/img/Evolve/facebook.png" alt="Facebook" />
              </a>
              <a
                v-if="redSocial(p, 'tiktok')"
                :href="redSocial(p, 'tiktok')"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
                aria-label="TikTok"
              >
                <i class="bi bi-tiktok"></i>
              </a>
              <a
                v-if="redSocial(p, 'youtube')"
                :href="redSocial(p, 'youtube')"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                <img src="@/assets/img/Evolve/youtube.png" alt="YouTube" />
              </a>
            </div>
          </a>
        </div>

        <p v-else-if="!cargando" class="patro-vacio">
          Muy pronto anunciaremos a nuestros patrocinadores.
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.patro-wrapper {
  position: relative;
  min-height: 100vh;
  padding: 0;
  overflow: hidden;
}
.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.75) contrast(1.2);
  z-index: 1;
}
.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(20, 4, 2, 0.75) 50%,
    rgba(0, 0, 0, 0.95) 100%
  );
  z-index: 2;
}
#patroParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}
.smoke-layer {
  position: absolute;
  inset: 0;
  background: src("/assets/backgrounds/image3.png");
  opacity: 0.8;
  mix-blend-mode: lighten;
  animation: smokeMovePatro 20s infinite linear;
  z-index: 4;
}
@keyframes smokeMovePatro {
  0% {
    transform: translateY(0) scale(1.2);
  }
  100% {
    transform: translateY(-20%) scale(1.25);
  }
}

.patro-content {
  position: relative;
  z-index: 20;
  margin-top: calc(80px + 1.5rem);
  padding-bottom: 4rem;
}
.patro-section {
  width: min(1200px, 92vw);
  margin: 0 auto;
  padding-top: 1.5rem;
}

/* ── Título de sección ── */
.titulo-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding-top: 0.5rem;
  padding-bottom: 2rem;
}
.titulo-linea {
  flex: 1;
  max-width: 120px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(204, 0, 0, 0.55),
    rgba(220, 60, 60, 0.5),
    rgba(204, 0, 0, 0.55),
    transparent
  );
}
.titulo-seccion {
  font-family: "Oswald", "Impact", sans-serif;
  font-size: clamp(1.4rem, 4vw, 2rem);
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 2px;
  white-space: nowrap;
  margin: 0;
}
.titulo-seccion em {
  font-style: normal;
  color: transparent;
  -webkit-text-stroke: 1.5px #cc0000;
}
@media (max-width: 480px) {
  .titulo-seccion {
    font-size: 1.2rem;
  }
}

.patro-grid {
  display: grid;
  gap: 1.4rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (min-width: 576px) {
  .patro-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (min-width: 992px) {
  .patro-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.6rem;
  }
}

.patro-card {
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(0, 170, 255, 0.22);
  border-radius: 18px;
  backdrop-filter: blur(10px);
  padding: 1.6rem 1.2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;
  color: inherit;
  cursor: default;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.02) inset, 0 10px 30px rgba(0, 0, 0, 0.25),
    0 0 22px rgba(220, 60, 60, 0.5);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.patro-card--link {
  cursor: pointer;
}
.patro-card:hover {
  transform: translateY(-6px);
  border-color: rgba(204, 0, 0, 0.55);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 14px 38px rgba(204, 0, 0, 0.35),
    0 0 30px rgba(204, 0, 0, 0.28);
}

.patro-logo-plate {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 10px;
  display: grid;
  place-items: center;
  padding: 0.9rem;
  margin-bottom: 0.9rem;
}
.patro-logo-plate img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.patro-nombre {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #ffffff;
}

.patro-redes {
  display: flex;
  gap: 0.7rem;
  margin-top: 0.6rem;
}
.patro-redes a img {
  width: 22px;
  height: 22px;
  filter: brightness(0.65);
  transition: filter 0.2s, transform 0.2s;
}
.patro-redes a:hover img {
  filter: brightness(1) drop-shadow(0 0 5px rgba(0, 170, 255, 0.55));
  transform: translateY(-3px);
}
/* TikTok no tiene asset PNG en el proyecto (ver FighterCard.vue); se deja el
   icon-font pero con el mismo tratamiento visual que los íconos en imagen. */
.patro-redes .bi-tiktok {
  font-size: 1.15rem;
  line-height: 1;
  color: rgba(255, 255, 255, 0.65);
  filter: brightness(0.65);
  transition: filter 0.2s, transform 0.2s, color 0.2s;
  display: inline-block;
}
.patro-redes a:hover .bi-tiktok {
  color: #fff;
  filter: brightness(1) drop-shadow(0 0 5px rgba(0, 170, 255, 0.55));
  transform: translateY(-3px);
}

.patro-vacio {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
}

@media (min-width: 768px) and (max-width: 991.98px) {
  .patro-content {
    max-width: 100% !important;
    padding-left: 2rem !important;
    padding-right: 2rem !important;
  }
}
@media (max-width: 576px) {
  .patro-content {
    margin-top: calc(70px + 1rem);
  }
  .patro-card {
    padding: 1.2rem 1rem;
  }
}
</style>
